import React, { useEffect } from 'react';
import { DemoContext, useDemoProvider } from '../hooks/useDemoV2';

interface DemoProviderProps {
  children: React.ReactNode;
}

type DemoProviderValue = ReturnType<typeof useDemoProvider>;

type DemoDebugWindow = typeof window & {
  __demoDebug?: {
    isDemo: DemoProviderValue['isDemo'];
    slug: DemoProviderValue['slug'];
    demoRestaurantId: DemoProviderValue['demoRestaurantId'];
    orders: DemoProviderValue['orders'];
    kpis: {
      orderCount: DemoProviderValue['orderCount'];
      todayOrdersCount: DemoProviderValue['todayOrdersCount'];
      revenueCents: DemoProviderValue['revenueCents'];
      kitchenQueue: DemoProviderValue['kitchenQueue'];
    };
  };
  __demoActions?: Pick<DemoProviderValue, 'createCounterOrder' | 'updateDemoOrderStatus'>;
};

export const DemoProvider: React.FC<DemoProviderProps> = ({ children }) => {
  const demoContextValue = useDemoProvider();

  const {
    isDemo,
    slug,
    demoRestaurantId,
    orders,
    orderCount,
    todayOrdersCount,
    revenueCents,
    kitchenQueue,
    createCounterOrder,
    updateDemoOrderStatus,
  } = demoContextValue;

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const target = window as DemoDebugWindow;

    try {
      target.__demoDebug = {
        isDemo,
        slug,
        demoRestaurantId,
        orders,
        kpis: {
          orderCount,
          todayOrdersCount,
          revenueCents,
          kitchenQueue,
        },
      };

      target.__demoActions = {
        createCounterOrder,
        updateDemoOrderStatus,
      };
    } catch (error) {
      console.error('[DemoProvider] Failed to publish debug helpers', error);
    }
  }, [
    isDemo,
    slug,
    demoRestaurantId,
    orders,
    orderCount,
    todayOrdersCount,
    revenueCents,
    kitchenQueue,
    createCounterOrder,
    updateDemoOrderStatus,
  ]);

  return (
    <DemoContext.Provider value={demoContextValue}>
      {children}
    </DemoContext.Provider>
  );
};
